import React, { useEffect, useMemo, useReducer, useState } from 'react';
import SwiperClass from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import { useWindowSize } from '@/components/use.window.size';

import { Header } from '@/components/header';
import { ProgressHeader } from '@/components/ProgressHeader';
import { FormPage } from '@/components/layouts/slider/page';
import { Field } from '@/components/layouts/slider/field';
import { LayoutProps } from '@/components/layouts/layout.props';
import { useSubmission } from '@/components/use.submission';
import SubmitModal from '@/components/SubmitModal';
import {
  ACTIONS,
  SubmissionsState,
  SubmissionsAction,
} from '@/components/layouts/layout.props';

const reducer = (
  submissions: SubmissionsState[],
  action: SubmissionsAction
) => {
  switch (action.type) {
    case ACTIONS.ADD_SUBMISSION:
      let replaced = false;
      const updated = submissions.map((submission: SubmissionsState) => {
        if (submission.field === action.payload.field) {
          replaced = true;
          return action.payload;
        } else {
          return submission;
        }
      });
      if (replaced) {
        return updated;
      } else {
        return [...updated, action.payload];
      }
    case ACTIONS.DELETE_SUBMISSIONS:
      return [];
    default:
      return submissions;
  }
};

const renderDefaultEndPage = (params: any) => {
  return (
    <FormPage
      page={params.page}
      className={params.className}
      design={params.design}
    />
  );
};

export const ProductShapeForm: React.FC<LayoutProps> = ({
  form,
  customFormJson,
  renderCustomFormEndPage = renderDefaultEndPage,
  setSubmissionId,
}) => {
  // const { t } = useTranslation();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const { height } = useWindowSize();
  const { title, id, design, startPage, endPage, fields, isLive } = form;
  const [submissionFlag, setSubmissionFlag] = useState(false); // flag to whether submit the submission or not
  const currentSubmission = useSubmission(id as string);
  const { startSubmission, finish, setField } = currentSubmission;
  const [submissions, dispatch] = useReducer(reducer, []); // overall submissions
  // used to reset answers of each question when user resets form
  // field.tsx - formik doesn't reset the form unless its intialValues prop changes
  const [submissionCounter, setSubmissionCounter] = useState(0);
  const [showProgress, setShowProgress] = useState(!startPage.show);
  const [progress, setProgress] = useState(0);
  const [categoryScores, setCategoryScores] = useState<{
    [key: string]: number;
  }>({});
  const [actualScores, setActualScores] = useState<any>();
  const [lastSlideIndex, setLastSlideIndex] = useState(fields.length - 1);

  //track total questions
  const totalQuestions = useMemo(
    () => fields.filter((field) => field.type !== 'hidden').length,
    []
  );

  useEffect(() => {
    document.title = title;

    // get the slide index of the last slide that is not hidden
    for (let i = fields.length - 1; i >= 0; i--) {
      if (fields[i].type !== 'hidden') {
        setLastSlideIndex(i);
        break;
      }
    }

    const initialCategoryScores: { [key: string]: number } = {};
    customFormJson.categories.forEach(
      (category: { name: string; maxScore: number }) => {
        initialCategoryScores[category.name] = 0;
      }
    );
    setCategoryScores(initialCategoryScores);
  }, []);

  useEffect(() => {
    const submitSubmission = async () => {
      const s: { id: string; token: string } | undefined =
        await startSubmission();
      if (setSubmissionId && s) {
        setSubmissionId(s?.id);
      }
      if (s) {
        await setField('', submissions, s).catch((err) => console.error(err));
        await finish(s);
      }
      setProgress(100);
      setShowProgress(false);
      setActualScores(getScores());
      setSubmissionFlag(false);
      // saving could be really fast so this prevents saving spinner from flashing
      await new Promise((r) => setTimeout(r, 1000));
      goNext();
    };
    if (submissionFlag) {
      submitSubmission();
    }
  }, [submissionFlag]);

  const addCategoryScore = (submission: {
    submissionId: string;
    value: string;
  }) => {
    // find the previous question submission if exists
    const prevSubmission = submissions.find(
      (item: SubmissionsState) => item.field === submission.submissionId
    );
    const submissionItem = customFormJson.questions.find(
      (item: { category: string; questionId: string }) =>
        item.questionId === submission.submissionId
    );

    if (submissionItem?.category) {
      if (prevSubmission) {
        // if current question submission already was calculated, subtract old score and add new
        setCategoryScores({
          ...categoryScores,
          [submissionItem.category]:
            categoryScores[submissionItem.category] -
            parseInt(prevSubmission.data.replace('"', '')) +
            parseInt(submission.value),
        });
      } else {
        // if not, just add
        setCategoryScores({
          ...categoryScores,
          [submissionItem.category]:
            categoryScores[submissionItem.category] +
            parseInt(submission.value),
        });
      }
    }
  };

  const getScores = (): number[] => {
    return Object.values(categoryScores).map(
      (score, i) => (score / customFormJson.categories[i].maxScore) * 3
    );
  };

  const goNext = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideNext();
    swiper.allowSlideNext = false;
  };
  const goPrev = () => {
    if (!swiper) return;

    // logger('goPrev')
    swiper.allowSlidePrev = true;
    swiper.slidePrev();
    swiper.allowSlidePrev = false;
  };
  const goBeginning = () => {
    if (!swiper) return;

    // logger('goNext')
    const resettedCategoryScores: { [key: string]: number } = {};
    Object.keys(categoryScores).forEach(
      (key) => (resettedCategoryScores[key] = 0)
    );
    setCategoryScores(resettedCategoryScores);
    setProgress(0);
    setShowProgress(!startPage.show);
    dispatch({
      type: ACTIONS.DELETE_SUBMISSIONS,
      payload: { field: '', data: '' },
    });
    setSubmissionCounter(submissionCounter + 1);

    swiper.allowSlidePrev = true;
    swiper.slideTo(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };

  const swiperConfig: SwiperProps = {
    direction: 'horizontal',
    allowSlideNext: false,
    allowSlidePrev: false,
    preventClicks: false,
    noSwiping: false,
    allowTouchMove: false,
    preventClicksPropagation: false,
    updateOnWindowResize: true,
    keyboard: false, // doesn't seem to do anything but will leave here, handleKeyPress is custom way of disable keyboard
  };

  const handleKeyPress = async (event: any) => {
    if (
      // prevent user from going forward from pressing enter key
      // however it will allow enter key to go through if the event came from mui textarea
      // this is to allow the textarea to add new lines
      event.key === 'Enter' &&
      !event.target.className.includes('MuiInputBase-inputMultiline')
    ) {
      event.preventDefault();
    } else if (
      // prevent user from going forward from pressing space key
      // howeever it will allow space key to go through if the event came from mui input
      // this is to allow text line, text area, etc to add space character
      event.key === ' ' &&
      !event.target.className.includes('MuiInputBase-input')
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      {submissionFlag && <SubmitModal design={design} />}
      <div
        className={'swiper-container'}
        style={{
          background: design.colors.background,
        }}
        onKeyDown={handleKeyPress}
      >
        {showProgress ? (
          <ProgressHeader
            progressValue={progress}
            steps={fields.length}
            design={design}
            reset={goBeginning}
          />
        ) : null}

        <Swiper
          autoHeight // for some reason causes speedup transition for start page?
          {...swiperConfig}
          onSwiper={(next) => {
            // logger('setSwiper')
            setSwiper(next);
          }}
        >
          {[
            startPage.show ? (
              <SwiperSlide key={'start'}>
                <Header design={design} />
                <FormPage
                  page={startPage}
                  className={'start-page'}
                  image={true}
                  design={design}
                  next={goNext}
                  prev={goPrev}
                  showProgress={setShowProgress}
                />
              </SwiperSlide>
            ) : undefined,
            ...fields
              .map((field, i) => {
                if (field.type === 'hidden') {
                  return null;
                }

                return (
                  <SwiperSlide key={field.id} className={'slide'}>
                    <Field
                      field={field}
                      focus={
                        swiper?.activeIndex === (startPage.show ? 1 : 0) + i
                      }
                      design={design}
                      save={(values: { [key: string]: unknown }) => {
                        const fieldObj = {
                          field: field.id,
                          data: JSON.stringify(
                            values[field.id] !== undefined &&
                              values[field.id] !== null
                              ? values[field.id]
                              : '-'
                          ),
                        };
                        const submissionObj = {
                          submissionId: field.id,
                          value: values[field.id] as string,
                        };

                        dispatch({
                          type: ACTIONS.ADD_SUBMISSION,
                          payload: fieldObj,
                        });
                        addCategoryScore(submissionObj);

                        if (i === lastSlideIndex && isLive) {
                          setSubmissionFlag(true);
                        }
                      }}
                      next={() => {
                        if (i === lastSlideIndex) {
                          // prevent going back!
                          swiper!.allowSlidePrev = false;
                          setShowProgress(false);
                        }
                        setProgress(
                          Math.floor(((i + 1) / fields.length) * 100)
                        );
                        goNext();
                      }}
                      prev={() => {
                        if (i !== 0) {
                          swiper!.allowSlideNext = false;
                          setProgress(
                            Math.floor(((i - 1) / fields.length) * 100)
                          );
                          goPrev();
                        }
                      }}
                      slideIndex={i}
                      submissionCount={submissionCounter}
                      lastSlideIndex={lastSlideIndex}
                      //added to display current question num out of total
                      totalQuestions={totalQuestions}
                    />
                  </SwiperSlide>
                );
              })
              .filter((e) => e !== null),
            !submissionFlag && progress === 100 && (
              <SwiperSlide key={'end'}>
                <Header design={design} />
                {renderCustomFormEndPage({
                  scores: actualScores,
                  design: design,
                  jsonData: customFormJson,
                  questions: fields,
                  userAnswers: submissions,
                  page: endPage,
                  className: 'end-page',
                })}
              </SwiperSlide>
            ),
          ].filter((e) => !!e)}
        </Swiper>
      </div>
    </>
  );
};
